// Sample a (small, field-scale) COG at many lon/lat points client-side — the fertility
// engine reads the modeled soil-property COGs at every stack square. Reads the WHOLE
// single-band raster once (field-scale ml-* COGs are ~1 MB) and does nearest-pixel
// lookups, which beats thousands of per-point range reads by orders of magnitude.
// CRS-aware: the raster's geokeys decide whether points need forward-projection
// (same EPSG mapping as the stack decoder).
import { epsgProjString } from "./stackClient";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

const MAX_PIXELS = 16_000_000; // ~64 MB float32 — anything bigger isn't a field COG

/* * Read one field COG once, then look up any lon/lat. ml-* rasters are EPSG:3857; lon/lat is projected in. */
export async function openCogSampler(
  url: string,
): Promise<(lng: number, lat: number) => number | null> {
  const [{ fromUrl }, proj4] = await Promise.all([
    import("geotiff"),
    import("proj4").then((m) => m.default),
  ]);
  const tiff = await fromUrl(url);
  const img = await tiff.getImage();
  const w = img.getWidth();
  const h = img.getHeight();
  if (w * h > MAX_PIXELS)
    throw new Error("This layer is too large to sample in the browser.");

  const gk = (img.getGeoKeys() ?? {}) as {
    ProjectedCSTypeGeoKey?: number;
    GeographicTypeGeoKey?: number;
  };
  const code = gk.ProjectedCSTypeGeoKey ?? gk.GeographicTypeGeoKey ?? 4326;
  let forward: ((p: [number, number]) => [number, number]) | null = null;
  if (code !== 4326) {
    const projStr = epsgProjString(code);
    if (!projStr || projStr === "unknown")
      throw new Error(`Unsupported raster CRS (EPSG:${code}).`);
    const conv = proj4(projStr);
    forward = (p) => conv.forward(p) as [number, number];
  }

  const [ox, oy] = img.getOrigin();
  const [rx, ry] = img.getResolution();
  const nodata = img.getGDALNoData();
  const rasters = await img.readRasters({ samples: [0] });
  const band = rasters[0] as unknown as ArrayLike<number>;

  return (lng: number, lat: number) => {
    const [x, y] = forward ? forward([lng, lat]) : [lng, lat];
    const c = Math.floor((x - ox) / rx);
    const r = Math.floor((y - oy) / ry);
    if (c < 0 || c >= w || r < 0 || r >= h) return null;
    const v = band[r * w + c];
    if (v == null || !Number.isFinite(v)) return null;
    if (nodata != null && v === nodata) return null;
    return v;
  };
}

export async function sampleCogAtPoints(
  url: string,
  pts: { lng: number; lat: number }[],
): Promise<(number | null)[]> {
  const sample = await openCogSampler(url);
  return pts.map(({ lng, lat }) => sample(lng, lat));
}

/*
 *
 * This reads the same single-band raster the sampler above already reads (field-scale, ~1 MB) and
 * hands back its pixel centres so the identical clustering runs over a raster and a vector grid
 * alike. `pixelAreaM2` comes back with it because the cluster-size floor is really an AREA — "20
 * cells" only means something on an 8 m grid.
 */
export async function cogValuePoints(
  url: string,
  opts: { maxSamples?: number } = {},
): Promise<{
  points: { lng: number; lat: number; value: number }[];
  pixelAreaM2: number;
}> {
  const maxSamples = opts.maxSamples ?? 60_000;
  const [{ fromUrl }, proj4] = await Promise.all([
    import("geotiff"),
    import("proj4").then((m) => m.default),
  ]);
  const tiff = await fromUrl(url);
  const img = await tiff.getImage();
  const w = img.getWidth();
  const h = img.getHeight();
  if (w * h > MAX_PIXELS)
    throw new Error("This layer is too large to sample in the browser.");

  const gk = (img.getGeoKeys() ?? {}) as {
    ProjectedCSTypeGeoKey?: number;
    GeographicTypeGeoKey?: number;
  };
  const code = gk.ProjectedCSTypeGeoKey ?? gk.GeographicTypeGeoKey ?? 4326;
  let inverse: ((p: [number, number]) => [number, number]) | null = null;
  if (code !== 4326) {
    const projStr = epsgProjString(code);
    if (!projStr || projStr === "unknown")
      throw new Error(`Unsupported raster CRS (EPSG:${code}).`);
    const conv = proj4(projStr);
    inverse = (p) => conv.inverse(p) as [number, number];
  }

  const [ox, oy] = img.getOrigin();
  const [rx, ry] = img.getResolution();
  const nodata = img.getGDALNoData();
  const rasters = await img.readRasters({ samples: [0] });
  const band = rasters[0] as unknown as ArrayLike<number>;

  // Stride rather than resample: a decimated read would average across a nodata edge and invent values
  // just inside the boundary, which is the one place we most need the numbers to be real.
  const stride = Math.max(1, Math.ceil(Math.sqrt((w * h) / maxSamples)));

  const points: { lng: number; lat: number; value: number }[] = [];
  for (let r = 0; r < h; r += stride) {
    for (let c = 0; c < w; c += stride) {
      const v = band[r * w + c];
      if (v == null || !Number.isFinite(v)) continue;
      if (nodata != null && v === nodata) continue;
      const x = ox + (c + 0.5) * rx;
      const y = oy + (r + 0.5) * ry;
      const [lng, lat] = inverse ? inverse([x, y]) : [x, y];
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      points.push({ lng, lat, value: v });
    }
  }

  // Pixel size in metres. A projected CRS is already metres; 4326 is degrees, so convert at this latitude.
  const midLat = points.length ? points[Math.floor(points.length / 2)].lat : 0;
  const pxM = inverse
    ? Math.abs(rx) * stride
    : Math.abs(rx) *
      stride *
      METRES_PER_DEGREE_LNG *
      Math.cos((midLat * Math.PI) / 180);
  const pyM = inverse
    ? Math.abs(ry) * stride
    : Math.abs(ry) * stride * METRES_PER_DEGREE_LAT;
  return { points, pixelAreaM2: Math.max(pxM * pyM, 1) };
}
